import {FC, useMemo, useState, Dispatch, SetStateAction } from 'react'
import { Notification, Person } from '../../../types/global/types'
import { Button } from "../../ContactsPage/Button"
import { BlueButton } from "../BlueButton"
import "./styles.scss"
import {UpdatedPersonNotification} from '../../Notifications/UpdatedPersonNotification';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { personSchema } from '../../../validationSchemas/personSchema';
import { addressSchema } from '../../../validationSchemas/addressSchema';
import {
  confirmUpdateUpdatedPersonNotification
} from '../../../backend/store/actions/creators/confirmUpdateUpdatedPersonsNotification';
import {generalActions} from '../../../backend/store/reducers/generalReducer';
import {actions as notificationActions, notificationsTriggerActions} from '../../../backend/store/reducers/notificationsReducer';
import {updateUpdatedPersonNotification} from '../../../backend/store/actions/creators/updatePersonNotification';
import { updatedHomeAddressNotification } from '../../../backend/store/actions/creators/updatedHomeAddressNotification';
import { useTranslation } from '../../../hooks/useTranslation';

type Props = {
  notification: Notification,
  setSelectedNotification: Dispatch<SetStateAction<Notification | null>>,
}

export const ContactInfoNotification: FC<Props> = ({
  notification,
  setSelectedNotification,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [isVisibleUpdatedInfoModal, setIsVisibleUpdatedInfoModal] = useState(false);

  // This notification may now also carry the household HOME details (home address + house
  // phone) for a non-left-town visit, so the admin confirms contact edits AND home details
  // with one click. A pure contact-info notification carries neither.
  const stagedPhone = (notification as any).newHousePhone;
  const hasStagedPhone = stagedPhone !== null && stagedPhone !== undefined;
  // A CORRECTION to the household's existing home address (household did NOT move) — its own
  // field/backend column (`updatedHomeAddress`), entirely separate from Left Town's
  // `newHouseholdAddress` (never shown on this notification type).
  const hasStagedAddress = !!(notification as any).updatedHomeAddress;

  // Applied after a successful confirm: toast, drop from the list + unread count, close the
  // modal, and return to the notification list.
  const finish = () => {
    dispatch(generalActions.successAC({ message: "Success", type: "success" }));
    dispatch(notificationActions.removeNotificationFromList(notification.id));
    dispatch(notificationsTriggerActions.getNotificationsCountTriggerAC());
    setIsVisibleUpdatedInfoModal(false);
    setSelectedNotification(null);
  };
  const fail = () => dispatch(generalActions.errorAC({ message: "Something went wrong", type: "error" }));

  const formik = useFormik<{ updated_persons: Person[]; updated_home_address: any; house_phone?: string }>({
    initialValues: {
      updated_persons: (notification.notes as any)?.updatedPersons || notification.updatedPersons || [],
      // The staged home-address CORRECTION (household did NOT move) so the admin can review/
      // correct it — always Home, always primary, so no person_id like Left Town's new_address.
      updated_home_address: {
        ...((notification as any).updatedHomeAddress || {}),
        type: 'Home',
        set_as_household_address: true,
      },
      house_phone: hasStagedPhone ? String(stagedPhone) : undefined,
    },
    enableReinitialize: true,
    validationSchema: Yup.object().shape({
      updated_persons: Yup.array().of(personSchema),
      updated_home_address: addressSchema,
    }),
    onSubmit: () => submitAll(),
  });

  const storedSalutations = useSelector(
      (state: any) => state.personsStore.salutations
  )

  const storedAddressesTypes = useSelector(
      (state: any) => state.addressesStore.types
  )

  const salutations = useMemo(
      () => storedSalutations || [],
      [storedSalutations]
  )
  const addressesTypes = useMemo(
      () => storedAddressesTypes || [],
      [storedAddressesTypes]
  );

  // One Confirm applies everything: persist the admin's home-detail edits, then the contact
  // edits, then confirm (the combined backend confirm applies persons AND home details).
  // A function declaration (hoisted) so the formik `onSubmit` above can reference it.
  function submitAll() {
    const confirmAll = () =>
      dispatch(confirmUpdateUpdatedPersonNotification({
        id: notification.id,
        data: formik.values.updated_persons,
        onSuccess: finish,
        onError: fail,
      }));

    const persistPersonsThen = (next: () => void) => {
      if ((formik.values.updated_persons || []).length > 0) {
        dispatch(updateUpdatedPersonNotification({
          id: notification.id,
          data: formik.values.updated_persons,
          onSuccess: next,
          onError: fail,
        }));
      } else {
        next();
      }
    };

    const persistHomeThen = (next: () => void) => {
      if (hasStagedAddress || hasStagedPhone) {
        dispatch(updatedHomeAddressNotification({
          id: notification.id,
          data: { ...formik.values.updated_home_address, house_phone: formik.values.house_phone },
          onSuccess: next,
          onError: fail,
        }));
      } else {
        next();
      }
    };

    persistHomeThen(() => persistPersonsThen(confirmAll));
  }

  return (
    <div
      className="notification"
    >
      <div className="notification__header">
        <div className="notification__title">
          <h3>
            {notification.title.startOfTitle}
            {notification.title.householdName}
            {notification.title.endOfTitle}
          </h3>
          <div className="notification__time">
            {notification.created_at_formatted}
          </div>
        </div>
      </div>

      {/* All staged changes — contact edits AND household home details (home address + house
          phone) — live in one consolidated "view details" panel, shown only on demand. */}
      <div className="notification__info">
        {t("notifications.view_and_change_staged")}
      </div>
      <div className="notification__info-button">
        <BlueButton title={t("common.view_details")} onClick={() => setIsVisibleUpdatedInfoModal(prevState => !prevState)} />
      </div>
      <div className="notification__button">
        <Button title={t("common.confirm_changes")} onClick={submitAll} />
      </div>
      <UpdatedPersonNotification
          isVisible={isVisibleUpdatedInfoModal}
          setIsVisible={() => {
            setIsVisibleUpdatedInfoModal(prevState => !prevState)
          }}
          formik={formik}
          addressesTypes={addressesTypes}
          salutations={salutations}
          hasStagedPhone={hasStagedPhone}
          hasStagedAddress={hasStagedAddress}
          contacts={(notification as any).householdContacts || []}
      />
    </div>
  )
}
